<template>
  <HeadTitle
    title="Licencias y permisos"
    description="Lista de las solicitudes de licencias y sus estados de aprobación"
  />
  <divider></divider>
  <Toolbar class="mb-4 md:w-11">
    <template #start> </template>
    <template #end>
      <Button
        label="Nueva solicitud"
        icon="pi pi-plus"
        severity="primary"
        class="mr-2 font-semibold text-sm"
        @click="openNewRequestDialog"
      />
    </template>
  </Toolbar>
  <DataTable
    v-model:expandedRows="expandedRows"
    :value="licenses"
    dataKey="id"
    @rowExpand="onRowExpand"
    tableStyle="min-width: 60rem"
    class="tabla mb-3"
    paginator
    :rows="5"
    :rowsPerPageOptions="[5, 10, 20, 50]"
    v-model:filters="filters"
    filter-display="row"
    paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
    currentPageReportTemplate="{first} a {last} de {totalRecords}"
    :globalFilterFields="[
      'licenseType',
      'observation',
      'quantity',
      'dateInit',
      'dateEnd',
      'createdAt',
    ]"
  >
    <template #header>
      <div class="flex justify-content-end">
        <span class="p-input-icon-left">
          <i class="pi pi-search" />
          <InputText v-model="filters['global'].value" placeholder="Buscar" />
        </span>
      </div>
    </template>
    <Column expander style="width: 5rem" />
    <Column
      field="globalStatus"
      header="Estado"
      :showFilterMenu="false"
      :filterMenuStyle="{ width: '3rem' }"
    >
      <template #body="{ data, field }">
        <Tag
          :value="formatRequestState(data[field])"
          :severity="getStatusLabel(data[field])"
          class=""
        ></Tag>
      </template>
      <template #filter="{ filterModel, filterCallback }">
        <Dropdown
          v-model="filterModel.value"
          @change="filterCallback()"
          :options="statuses"
          placeholder="filtrar estado"
          class="p-column-filter flex align-items-center"
          style="min-width: 12rem; height: 3rem"
          :showClear="true"
        >
          <template #value="{ value }">
            <span class="text-sm text-600 font-medium">
              {{ value && formatRequestState(value) }}
            </span>
          </template>
          <template #option="slotProps">
            <Tag
              :value="formatRequestState(slotProps.option)"
              :severity="getStatusLabel(slotProps.option)"
            />
          </template>
        </Dropdown>
      </template>
    </Column>
    <Column header="Tipo de licencia" field="licenseType" sortable>
      <template #body="{ data, field }">
        {{ data[field] }}
      </template>
    </Column>
    <Column header="Observación" field="observation">
      <template #body="{ data, field }">
        {{ data[field] }}
      </template>
    </Column>
    <Column header="Cantidad" field="quantity" sortable>
      <template #body="{ data, field }">
        {{ data[field] }}
      </template>
    </Column>
    <Column header="Fecha de inicio" field="dateInit" sortable></Column>
    <Column header="Fecha final" field="dateEnd" sortable></Column>
    <Column header="Aprobadores">
      <template #body="{ data }">
        {{ data.approvedCount }} / {{ data.approversCount }}
      </template>
    </Column>
    <Column field="createdAt" header="Fecha de creacion"></Column>
    <Column header="Acciones">
      <template #body="{ data }">
        <div class="actions">
          <ConfirmPopup></ConfirmPopup>
          <template v-if="data.files && data.files[0] !== ''">
            <Button
              v-for="file of data.files"
              v-tooltip.bottom="'ver adjunto'"
              class="action-btn view hover:text-blue-800 mr-2"
              severity="secondary"
              icon="pi pi-paperclip"
              @click="downloadAttachment(file)"
            ></Button>
          </template>
          <Button
            v-if="
              data.approvedCount === 0 &&
              data.globalStatus === 'PENDING' &&
              data.origin === 'NATIVE'
            "
            v-tooltip.bottom="'Eliminar solicitud'"
            class="action-btn view btn hover:text-blue-800"
            severity="danger"
            icon="pi pi-trash"
            @click="deleteConfirm($event, data['id'])"
          ></Button>
        </div>
      </template>
    </Column>
    <template #expansion="slotProps">
      <div class="p-3">
        <h5 class="mb-1">Detalle de aprobadores</h5>
        <DataTable :value="slotProps.data.steps">
          <template
            #empty
            v-if="slotProps.data.origin !== 'NATIVE'"
            class="flex flex-justify-center"
          >
            Solicitud creada desde el sistema de nómina
          </template>
          <template #empty v-else class="flex flex-justify-center">
            <i class="pi pi-spin pi-spinner" style="font-size: 1rem"></i>
            cargando...
          </template>
          <Column field="approver.name" header="Nombre"></Column>
          <Column field="resolutionDate" header="Fecha de resolución">
            <template #body="{ data, field }">
              <span v-if="data[field]" v-local-date="data[field]"></span>
              <span class="text-600 font-medium" v-else>Sin definir</span>
            </template>
          </Column>
          <Column field="weight" header="Prioridad"></Column>
          <Column field="resolutionStatus" header="Estado">
            <template #body="slotProps">
              <Tag
                :value="formatRequestState(slotProps.data.resolutionStatus)"
                :severity="getStatusLabel(slotProps.data.resolutionStatus)"
              >
              </Tag>
            </template>
          </Column>
          <Column field="resolutionComment" header="Comentario"> </Column>
        </DataTable>
      </div>
    </template>
  </DataTable>
  <Dialog
    v-model:visible="licenseDialog"
    :style="{ width: '450px' }"
    header="Nueva solicitud de licencia o permiso"
    :modal="true"
    class="p-fluid"
  >
    <div class="field">
      <label for="licenseType" class="font-medium text-700"
        >Tipo de licencia <span class="text-red-500">*</span></label
      >
      <Dropdown
        id="licenseType"
        v-model="license.licenseType"
        :options="licenseDefinitions?.licensesTypes"
        required="true"
        placeholder="Selecciona un tipo de licencia"
        :loading="!licenseDefinitions?.licensesTypes"
        @change="onLicenseTypeChange"
      >
        <template #value="{ value, placeholder }">
          <span v-if="value" class="text-sm text-600 font-medium">
            {{ value.entity + ' - ' + value.name }}
          </span>
          <span v-else>
            {{ placeholder }}
          </span>
        </template>
        <template #option="{ option }">
          {{ option.entity + ' - ' + option.name }}
        </template>
      </Dropdown>
    </div>
    <div class="field" v-if="license?.licenseType?.enable_continuity">
      <label for="licenseNature" class="font-medium text-700"
        >Naturaleza de la licencia <span class="text-red-500">*</span></label
      >
      <Dropdown
        id="licenseNature"
        v-model="license.licenseNature"
        :options="licenseDefinitions?.licenseNatures"
        required="true"
        placeholder="Selecciona una naturaleza de licencia"
      >
        <template #value="{ value, placeholder }">
          <span v-if="value" class="text-sm text-600 font-medium">
            {{ value.name }}
          </span>
          <span v-else>
            {{ placeholder }}
          </span>
        </template>
        <template #option="{ option }">
          {{ option.name }}
        </template>
      </Dropdown>
    </div>
    <div class="field">
      <label for="dataInit" class="font-medium text-700"
        >Fecha inicial <span class="text-red-500">*</span></label
      >
      <Calendar
        id="dataInit"
        v-model="license.dateInit"
        required="true"
        :disabled="license.licenseType === null"
        :class="{ 'p-invalid': submitted && !license.dateInit }"
        :disabledDays="getDisabledDays"
        :disabled-dates="getDisabledDates"
        date-format="dd/mm/yy"
        @blur="calculateDateEnd"
      >
        <template #date="{ date }">
          <div class="flex flex-column">
            <span>{{ date.day }}</span>
            <span :class="getBadge(date)"></span>
          </div>
        </template>
        <template #footer>
          <div class="ml-6 mt-3">
            <div class="flex align-items-center">
              <div class="mr-2 license-pending"></div>
              <label class="text-sm font-semibold text-600 mt-2"
                >Licencias pendientes</label
              >
            </div>

            <div class="flex align-items-center">
              <div class="mr-2 license-approved"></div>
              <label class="text-sm font-semibold text-600 mt-2"
                >Licencias aprobadas</label
              >
            </div>

            <div class="flex align-items-center">
              <div class="mr-2 vacation-approved"></div>
              <label class="text-sm font-semibold text-600 mt-2"
                >Vacaciones aprobadas</label
              >
            </div>

            <div class="flex align-items-center">
              <div class="mr-2 vacation-pending"></div>
              <label class="text-sm font-semibold text-600 mt-2"
                >Vacaciones pendientes</label
              >
            </div>

            <div class="flex align-items-center">
              <div class="mr-2 holiday"></div>
              <label class="text-sm font-semibold text-600 mt-2"
                >Feriados</label
              >
            </div>
          </div>
        </template>
      </Calendar>
    </div>
    <div class="field">
      <label for="cantDays" class="font-medium text-700"
        >Cantidad de dias <span class="text-red-500">*</span></label
      >
      <InputNumber
        id="cantDays"
        v-model="license.quantityDays"
        :class="{ 'p-invalid': submitted && !license.quantityDays }"
        @blur="calculateDateEnd"
      />
    </div>
    <div class="field">
      <label for="dateEnd" class="font-medium text-700"
        >Fecha final <span class="text-red-500">*</span></label
      >
      <Calendar
        id="dateEnd"
        v-model="license.dateEnd"
        :class="{ 'p-invalid': submitted && !license.dateEnd }"
        disabled
        date-format="dd/mm/yy"
      />
    </div>
    <div class="field" v-if="license.licenseType?.allow_primary_attachment">
      <label for="adjunto" class="font-medium text-700"
        >Adjuntar documento(s)
        <span
          v-if="license.licenseType.obligatory_primary_attachment"
          class="text-red-500"
          >*</span
        ></label
      >
      <FileUpload
        mode="basic"
        class="font-light text-sm w-auto text-center"
        style="font-size: 12px !important"
        custom-upload
        @uploader="removeFile"
        @select="saveFile($event)"
        :multiple="true"
        upload-icon="pi pi-paperclip"
      />
    </div>
    <div class="field">
      <label for="observation" class="font-medium text-700"
        >Observación <span class="text-red-500">*</span></label
      >
      <Textarea
        id="observation"
        v-model="license.observation"
        required="true"
        :class="{ 'p-invalid': submitted && !license.observation }"
      />
    </div>
    <template #footer>
      <Button label="Cancelar" icon="pi pi-times" text @click="hideDialog" />
      <Button
        label="Solicitar"
        icon="pi pi-check"
        text
        @click="saveRequest"
        :disabled="!isValidToSave"
      />
    </template>
  </Dialog>
</template>
<script setup lang="ts">
  import Toolbar from 'primevue/toolbar'
  import { useUserStore } from '@/store'
  import { storeToRefs } from 'pinia'
  import { FilterMatchMode } from 'primevue/api'
  import { computed, onMounted, ref } from 'vue'
  import Tag from 'primevue/tag'
  import useNotifications from '@/store/notification'
  import useLoading from '@/store/load'
  import { getApprovalFlowStepsByRequest } from '@/modules/approvers/services/ApprovalRequestService'
  import { getFileUrlPresigned } from '@/shared/services/fileService'
  import { WorkingDay } from '../models/WokingDay'
  import { Holiday } from '../models/Holiday'
  import { useConfirm } from 'primevue/useconfirm'
  import License from '../models/License'
  import HeadTitle from '@/shared/components/HeadTitle.vue'
  import {
    getLicensesByEmployeeId,
    getLicenseTypes,
    getDaysByLicenseType,
    getDateEnd,
    createLicenseRequest,
    deleteLicenseById,
  } from '../services/LicenseService'
  import { formatDate } from '@/shared/utils/formatDate'
  import GetLicenseTypeDto from '../services/interfaces/GetLicenseTypeDto'
  import LicenseForm from '../models/LicenseForm'
  import Vacation from '../models/Vacation'
  import { getVacationsByEmployeeId } from '../services/vacationService'
  import dayjs from 'dayjs'
  const { user } = storeToRefs(useUserStore())

  const confirm = useConfirm()
  const licenses = ref<License[]>()
  const vacations = ref<Vacation[]>()
  const statuses = ref(['PENDING', 'APPROVED', 'REJECTED'])
  const licenseDialog = ref(false)
  const submitted = ref(false)
  const notificationStore = useNotifications()
  const loadingStore = useLoading()
  const workinDays = ref<WorkingDay[]>([])
  const holidays = ref<Holiday[]>([])
  const vacationsPending = ref<Date[]>([])
  const vacationsApproved = ref<Date[]>([])
  const licensesApproved = ref<Date[]>([])
  const licensesPending = ref<Date[]>([])
  const licenseDefinitions = ref<GetLicenseTypeDto>()

  let license = ref<LicenseForm>({
    dateInit: undefined,
    dateEnd: undefined,
    quantityDays: 0,
    origin: 'NATIVE',
    observation: '',
    employeeId: user.value?.employeeId || '',
    licenseType: null,
    files: [],
    licenseNature: null,
    daysDetail: [],
  })

  onMounted(async () => {
    await refreshData()
  })

  const saveFile = (event: any) => {
    const files = event.files
    license.value.files = files
  }

  const removeFile = () => {
    license.value.files = []
  }

  const refreshData = async () => {
    loadingStore.setLoadingState()
    const [licensesResp, vacationResp] = await Promise.all([
      getLicensesByEmployeeId(user.value?.employeeId || ''),
      getVacationsByEmployeeId(user.value?.employeeId || ''),
    ])

    licenses.value = licensesResp.data.map((l) => ({
      ...l,
      dateInit: formatDate(l.dateInit),
      dateEnd: formatDate(l.dateEnd),
      createdAt: dayjs(l.createdAt).format('DD/MM/YYYY HH:mm'),
    }))

    vacations.value = vacationResp.data
    loadingStore.dismissLoadingState()
  }

  const expandedRows = ref([])

  const onRowExpand = async (event: any) => {
    if (event.data.origin !== 'NATIVE') {
      return
    }

    const requests = await getApprovalFlowStepsByRequest({
      approvalFlowId: event.data.approvalFlowId,
      approvalFlowRequestId: event.data.approvalFlowRequestId,
      employeeId: user.value?.employeeId || '',
    })

    licenses.value?.forEach((license: any) => {
      if (license.id === event.data.id) {
        license.steps = requests
      }
    })
  }

  const downloadAttachment = async (fileKey: string) => {
    if (!fileKey) return

    if (fileKey.includes('http')) {
      window.open(fileKey, '_blank')
      return
    }

    const fileUrl = await getFileUrlPresigned(fileKey)
    window.open(fileUrl, '_blank')
  }

  const onLicenseTypeChange = async (event: any) => {
    loadingStore.setLoadingState()
    const response = await getDaysByLicenseType({
      employeeId: user.value?.employeeId || '',
      licenseTypeId: license.value.licenseType?.id || '',
    })
    loadingStore.dismissLoadingState()
    holidays.value = response.holidays
    workinDays.value = response.workingDays
    clearDialog()
  }

  const deleteConfirm = (event: any, id: string) => {
    confirm.require({
      target: event.currentTarget,
      message: '¿Estás  seguro de eliminar esta solicitud?',
      icon: 'pi pi-exclamation-triangle',
      acceptClass: 'p-button-danger p-button-sm',
      acceptLabel: 'Si',
      rejectLabel: 'No',
      accept: async () => {
        const response = await deleteLicenseById(id)
        if (response === 'OK') {
          await refreshData()
          notificationStore.addSuccesNotification({
            detail: `200`,
            severity: 'success',
            summary: `Solicitud de licencia eliminada con exito`,
          })
        }
      },
      reject: () => {},
    })
  }

  const formatRequestState = (state: string) => {
    switch (state) {
      case 'PENDING':
        return 'Pendiente'
      case 'REJECTED':
        return 'Rechazado'
      case 'APPROVED':
        return 'Aprobado'
      default:
        return 'Pendiente'
    }
  }

  const openNewRequestDialog = async () => {
    licenseDialog.value = true
    if (workinDays.value?.length === 0 && holidays.value?.length === 0) {
      loadingStore.setLoadingState()
      getLicenseTypes().then((response) => {
        licenseDefinitions.value = response
      })
      loadingStore.dismissLoadingState()
    }
  }

  const getBadge = (dateRaw: any) => {
    const date = new Date(dateRaw.year, dateRaw.month, dateRaw.day)

    if (
      vacationsPending.value.some(
        (pendingDate) => pendingDate.getTime() === date.getTime()
      )
    ) {
      return 'vacation-pending'
    }

    if (
      vacationsApproved.value.some(
        (approvedDate) => approvedDate.getTime() === date.getTime()
      )
    ) {
      return 'vacation-approved'
    }

    if (
      licensesPending.value.some(
        (pendingDate) => pendingDate.getTime() === date.getTime()
      )
    ) {
      return 'license-pending'
    }

    if (
      licensesApproved.value.some(
        (approvedDate) => approvedDate.getTime() === date.getTime()
      )
    ) {
      return 'license-approved'
    }

    const dateString = date.toISOString().split('T')[0]

    if (holidays.value?.find((h) => h.date.split('T')[0] === dateString)) {
      return 'holiday'
    }

    return ''
  }

  const saveRequest = async () => {
    loadingStore.setLoadingState()
    const response = await createLicenseRequest({
      dateInit: license.value.dateInit || new Date(),
      dateEnd: license.value.dateEnd || new Date(),
      daysDetail: license.value.daysDetail,
      quantityDays: license.value.quantityDays,
      origin: 'NATIVE',
      observation: license.value.observation,
      employeeId: user.value?.employeeId || '',
      licenseTypeId: license.value.licenseType?.id || '',
      licenseType: license.value.licenseType?.name || '',
      licenseNatureId: license.value.licenseNature?.id || '',
      licenseNature: license.value.licenseNature?.name || '',
      files: license.value.files,
    })
    loadingStore.dismissLoadingState()

    if (response === 'OK') {
      notificationStore.addSuccesNotification({
        detail: `200`,
        severity: 'success',
        summary: `Solicitud de ${license.value.licenseType?.name} creada con exito`,
      })

      await refreshData()
    }

    loadingStore.dismissLoadingState()
    hideDialog()
  }

  const hideDialog = () => {
    clearDialog()
    licenseDialog.value = false
  }

  const clearDialog = () => {
    license.value = {
      dateInit: undefined,
      dateEnd: undefined,
      quantityDays: 0,
      origin: 'NATIVE',
      observation: '',
      employeeId: user.value?.employeeId || '',
      licenseType: license.value.licenseType,
      files: [],
      licenseNature: null,
      daysDetail: [],
    }
  }

  const calculateDateEnd = async () => {
    setTimeout(async () => {
      if (
        license.value.quantityDays > 0 &&
        license.value.dateInit &&
        license.value.dateInit.getTime() > 0
      ) {
        loadingStore.setLoadingState()
        const response = await getDateEnd({
          employeeId: user.value?.employeeId || '',
          licenseTypeId: license.value.licenseType?.id || '',
          days: String(license.value.quantityDays),
          startDate: license.value.dateInit.toISOString().split('T')[0],
        })

        license.value.dateEnd = new Date(response.endDate + 'T00:00:00')

        const datesNoAvailables = [
          ...vacationsPending.value,
          ...vacationsApproved.value,
          ...licensesPending.value,
          ...licensesApproved.value,
        ].map((date) => date.toISOString().split('T')[0])

        if (
          response.dayToDay.some((date) => datesNoAvailables.includes(date))
        ) {
          notificationStore.addSuccesNotification({
            detail: `400`,
            severity: 'warn',
            summary: `Hay fechas no disponibles en el rango de fechas seleccionado`,
          })

          license.value.dateEnd = undefined
          license.value.quantityDays = 0
        }
        license.value.daysDetail = response.dayToDay
        loadingStore.dismissLoadingState()
      }
    }, 300)
  }

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'APPROVED':
        return 'success'

      case 'PENDING':
        return 'warning'

      case 'REJECTED':
        return 'danger'

      default:
        return 'warning'
    }
  }

  const getDisabledDays = computed(() => {
    const allDays = [1, 2, 3, 4, 5, 6, 0]

    workinDays.value?.forEach((day) => {
      if (day.isoWeekDay === 7) {
        day.isoWeekDay = 0 // Cambiamos el valor de 7 a 0 (domingo) para que se muestre correctamente en el calendario
      }
      // Verificamos si day.isoWeekDay está definido y es un número entre 0 y 6
      if (
        typeof day.isoWeekDay === 'number' &&
        allDays.includes(day.isoWeekDay)
      ) {
        // Eliminamos el día de la lista de días disponibles
        const index = allDays.indexOf(day.isoWeekDay)
        if (index !== -1) {
          allDays.splice(index, 1)
        }
      }
    })

    return allDays
  })

  const getDisabledDates = computed(() => {
    const disabledDates: Date[] = []

    holidays.value?.forEach((holiday) => {
      disabledDates.push(new Date(holiday.date.split('T')[0] + 'T00:00:00'))
    })

    vacations.value
      ?.filter((v) => v.globalStatus === 'PENDING')
      .forEach((v) => {
        v.daysDetail?.forEach((d) => {
          disabledDates.push(new Date(d + 'T00:00:00'))
          vacationsPending.value.push(new Date(d + 'T00:00:00'))
        })
      })

    vacations.value
      ?.filter((v) => v.globalStatus === 'APPROVED')
      .forEach((v) => {
        v.daysDetail?.forEach((d) => {
          disabledDates.push(new Date(d + 'T00:00:00'))
          vacationsApproved.value.push(new Date(d + 'T00:00:00'))
        })
      })

    licenses.value
      ?.filter((v) => v.globalStatus === 'PENDING')
      .forEach((v) => {
        v.daysDetail?.forEach((d) => {
          disabledDates.push(new Date(d + 'T00:00:00'))
          licensesPending.value.push(new Date(d + 'T00:00:00'))
        })
      })

    licenses.value
      ?.filter((v) => v.globalStatus === 'APPROVED')
      .forEach((v) => {
        v.daysDetail?.forEach((d) => {
          disabledDates.push(new Date(d + 'T00:00:00'))
          licensesApproved.value.push(new Date(d + 'T00:00:00'))
        })
      })

    return disabledDates
  })

  const filters = ref({
    globalStatus: { value: null, matchMode: FilterMatchMode.EQUALS },
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
  })

  const isValidToSave = computed(() => {
    return (
      license.value.dateInit &&
      license.value.dateEnd &&
      license.value.quantityDays > 0 &&
      license.value.observation &&
      license.value.licenseType &&
      (license.value.licenseType.allow_primary_attachment &&
      license.value.licenseType.obligatory_primary_attachment
        ? license.value.files.length > 0
        : true)
    )
  })
</script>
<style scoped>
  .actions {
    display: flex;
    width: auto;
    flex-direction: row;
  }

  .action-btn {
    height: 20px;
    width: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .vacation-approved {
    background-color: #4caf50;
    border-radius: 50%;
    height: 10px;
    width: 10px;
    border: 1px solid #4caf50;
  }

  .vacation-pending {
    background-color: #ff9800;
    border-radius: 50%;
    height: 10px;
    width: 10px;
    border: 1px solid #ff9800;
  }

  .license-approved {
    background-color: rgb(228, 57, 57);
    border-radius: 50%;
    height: 10px;
    width: 10px;
    border: 1px solid rgb(228, 57, 57);
  }

  .license-pending {
    background-color: rgb(240, 243, 56);
    border-radius: 50%;
    height: 10px;
    width: 10px;
    border: 1px solid rgb(240, 243, 56);
  }

  .holiday {
    background-color: rgb(56, 56, 243);
    border-radius: 50%;
    height: 10px;
    width: 10px;
    border: 1px solid rgb(56, 56, 243);
  }

  tabla {
    margin-top: 1rem;
    box-shadow: 0px 1px 7px rgba(0, 0, 0, 0.1),
      0px 4px 5px -2px rgba(0, 0, 0, 0.12),
      0px 10px 15px -5px rgba(0, 0, 0, 0.2);
    border-radius: 16px;
    width: 90%;
  }

  :deep(.p-column-title) {
    font-size: 14px;
  }

  :deep(.p-datatable-tbody) {
    font-size: 14px;
  }
</style>
